// Import confidence calculator functions
import { 
	calculateOverallConfidence, 
	calculateConfidenceWithBreakdown,
	calculateSharpBookCoverage,
	getConfidenceLevel 
} from './utils/confidenceCalculator';

export function descendingComparator(a, b, orderBy) {
	// console.log('a: ', a, orderBy, b[orderBy], a[orderBy], b[orderBy] < a[orderBy]);
	if (orderBy === 'game') {
		const dateA = new Date(a.start_date);
		const dateB = new Date(b.start_date);
		return dateB - dateA; // descending
	} else if (orderBy === 'bet_price') {
		if (Number(b.bet_price) < Number(a.bet_price)) {
			return -1;
		}
		if (Number(b.bet_price) > Number(a.bet_price)) {
			return 1;
		}
		return 0;
	} else if (orderBy === 'confidence') {
		// Calculate confidence for both rows
		const confidenceA = calculateOverallConfidence(a, 0, 'WagerWiz'); // Market average not needed for sorting
		const confidenceB = calculateOverallConfidence(b, 0, 'WagerWiz');
		return confidenceA - confidenceB; // ascending (high to low on first click due to negation)
	} else if (orderBy === 'limit') {
		// Get limit values, handling both array and object cases
		let limitA = 0;
		let limitB = 0;

		if (a.limit_sharp) {
			if (Array.isArray(a.limit_sharp)) {
				for (const l of a.limit_sharp) {
					if (l?.max) {
						limitA = l.max;
						break;
					}
				}
			} else {
				limitA = a.limit_sharp?.max || 0;
			}
		}

		if (b.limit_sharp) {
			if (Array.isArray(b.limit_sharp)) {
				for (const l of b.limit_sharp) {
					if (l?.max) {
						limitB = l.max;
						break;
					}
				}
			} else {
				limitB = b.limit_sharp?.max || 0;
			}
		}
		
		if (limitB < limitA) {
			return -1;
		}
		if (limitB > limitA) {
			return 1;
		}
		return 0;
	} else {
		if (b[orderBy] < a[orderBy]) {
			return -1;
		}
		if (b[orderBy] > a[orderBy]) {
			return 1;
		}
		return 0;
	}
}

export function devigOddsProbit(overOdds, underOdds) {
    // Convert American odds to implied probabilities
    function americanToImpliedProb(americanOdds) {
        if (americanOdds > 0) {
            return 100 / (americanOdds + 100);
        } else {
            return Math.abs(americanOdds) / (Math.abs(americanOdds) + 100);
        }
    }

    // Convert implied probability back to American odds
    function impliedProbToAmerican(impliedProb) {
        if (impliedProb < 0.5) {
            return Math.round((100 / impliedProb) - 100);
        } else {
            return Math.round(-100 * impliedProb / (1 - impliedProb));
        }
    }

    // Get implied probabilities for both sides
    const overProb = americanToImpliedProb(overOdds);
    const underProb = americanToImpliedProb(underOdds);

    // Calculate total probability (includes vig)
    const totalProb = overProb + underProb;

    // Remove vig by normalizing probabilities
    const deviggedOverProb = overProb / totalProb;
    const deviggedUnderProb = underProb / totalProb;

    // Convert back to American odds
    const deviggedOverOdds = impliedProbToAmerican(deviggedOverProb);
    const deviggedUnderOdds = impliedProbToAmerican(deviggedUnderProb);

    return {
        overOdds: deviggedOverOdds,
        underOdds: deviggedUnderOdds
    };
}

// export const getLineMovementDirection = (historical_data) => {
// 	console.log('historical_data: ', historical_data);

// 	const startdate = new Date();
// 	const durationInMinutes = 2;
// 	startdate.setMinutes(new Date().getMinutes() - durationInMinutes);
// 	const endDate = new Date();
// 	endDate.setMinutes(new Date().getMinutes() - (durationInMinutes + 25));

// 	const data = historical_data?.filter((d) => new Date(d.time) < startdate && new Date(d.time) > endDate);
// 	if (data?.length) {
// 		try {
// 			const finalDataPoint = data[historical_data.length - 1];
// 			const secondFinalDataPoint = data[0];
// 			if (!secondFinalDataPoint || !finalDataPoint) {
// 				// no movement
// 				return 0;
// 			} else if (finalDataPoint.price > secondFinalDataPoint.price) {
// 				// direction is up
// 				return 1;
// 			} else if (finalDataPoint.price < secondFinalDataPoint.price) {
// 				// direction is down
// 				return -1;
// 			} else {
// 				// no movement
// 				return 0;
// 			}
// 		} catch (e) {
// 			return 0;
// 		}
// 	} else {
// 		try {
// 			const finalDataPoint = historical_data[historical_data.length - 1];
// 			const secondFinalDataPoint = historical_data[historical_data.length - 2];

// 			if (!secondFinalDataPoint || !finalDataPoint) {
// 				// no movement
// 				return 0;
// 			} else if (finalDataPoint.price > secondFinalDataPoint.price) {
// 				// direction is up
// 				return 1;
// 			} else if (finalDataPoint.price < secondFinalDataPoint.price) {
// 				// direction is down
// 				return -1;
// 			} else {
// 				// no movement
// 				return 0;
// 			}
// 		} catch (e) {
// 			// console.log(e, 'no movement');
// 			return 0;
// 		}
// 	}
// };

export function getLineMovementDirection(data) {
	if (!Array.isArray(data) || data.length < 2) {
		return 0; // Not enough data to determine a trend
	}

	const now = new Date();
	const tenMinutesAgo = new Date(now.getTime() - 10 * 60 * 1000);
	const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

	// Support both 'time' and 'timestamp' field names
	const getTimestamp = (entry) => entry.timestamp || entry.time;

	// Filter data points from the last hour
	let hourData = data.filter((entry) => {
		const entryTime = getTimestamp(entry);
		return entryTime && new Date(entryTime) >= oneHourAgo;
	});
	
	// Filter data points from the last 10 minutes
	let recentData = data.filter((entry) => {
		const entryTime = getTimestamp(entry);
		return entryTime && new Date(entryTime) >= tenMinutesAgo;
	});

	// If we don't have enough hour data, use all available data
	if (hourData.length < 2) {
		hourData = data.slice(-Math.max(4, data.length));
	}

	// If we don't have enough recent data, use the last few points
	if (recentData.length < 2) {
		recentData = data.slice(-Math.min(4, data.length));
	}

	if (hourData.length < 2 || recentData.length < 2) {
		return 0; // Not enough data to determine a trend
	}

	// Calculate overall hour trend
	const hourFirstPrice = hourData[0].price;
	const hourLastPrice = hourData[hourData.length - 1].price;
	const hourChange = hourLastPrice - hourFirstPrice;
	const hourPercentChange = hourFirstPrice !== 0 ? Math.abs(hourChange) / Math.abs(hourFirstPrice) : 0;
	const hourDirection = hourChange > 0 ? 1 : (hourChange < 0 ? -1 : 0);

	// Calculate recent 10-minute trend (weighted more heavily)
	const recentFirstPrice = recentData[0].price;
	const recentLastPrice = recentData[recentData.length - 1].price;
	const recentChange = recentLastPrice - recentFirstPrice;
	const recentPercentChange = recentFirstPrice !== 0 ? Math.abs(recentChange) / Math.abs(recentFirstPrice) : 0;
	const recentDirection = recentChange > 0 ? 1 : (recentChange < 0 ? -1 : 0);

	// Threshold to filter out rounding errors (increased from 0.1% to 0.5% to reduce false positives for flat lines)
	const MOVEMENT_THRESHOLD = 0.005;

	// Detect oscillation: if price is bouncing between values, it's not a clear trend
	// Count direction changes in recent data
	let directionChanges = 0;
	for (let i = 1; i < recentData.length - 1; i++) {
		const prevChange = recentData[i].price - recentData[i - 1].price;
		const nextChange = recentData[i + 1].price - recentData[i].price;
		if ((prevChange > 0 && nextChange < 0) || (prevChange < 0 && nextChange > 0)) {
			directionChanges++;
		}
	}

	// Check if oscillating - if there are multiple direction changes, it's likely oscillating
	let isOscillating = false;
	if (recentData.length >= 4 && directionChanges >= 2) {
		// Check if the oscillation is within a small range (likely noise/rounding)
		const recentPrices = recentData.map(d => d.price);
		const minPrice = Math.min(...recentPrices);
		const maxPrice = Math.max(...recentPrices);
		const priceRange = maxPrice - minPrice;
		const netChange = Math.abs(recentLastPrice - recentFirstPrice);
		const rangePercent = recentFirstPrice !== 0 ? priceRange / Math.abs(recentFirstPrice) : 0;
		
		// If oscillating within a small range (< 2.5%), consider it no movement
		// Also check if net change is similar to the range (indicating oscillation, not trend)
		// This catches cases like 2.2 -> 2.25 -> 2.2 -> 2.25 where it's just bouncing
		if (rangePercent < 0.025 || (netChange <= priceRange * 1.1 && rangePercent < 0.05)) {
			isOscillating = true;
		}
	}

	// If oscillating, require a stronger threshold to report movement
	const effectiveThreshold = isOscillating ? MOVEMENT_THRESHOLD * 3 : MOVEMENT_THRESHOLD;

	// Check if either trend meets the effective threshold
	const hourMeetsThreshold = hourPercentChange >= effectiveThreshold;
	const recentMeetsThreshold = recentPercentChange >= effectiveThreshold;

	// If neither trend meets the threshold, return 0 (no movement)
	if (!hourMeetsThreshold && !recentMeetsThreshold) {
		return 0; // Change is too small, likely rounding error
	}

	// Calculate weighted direction
	// Recent 10 minutes gets 70% weight, overall hour gets 30% weight
	// Use the magnitude of change to determine strength
	const hourMagnitude = hourMeetsThreshold ? hourPercentChange : 0;
	const recentMagnitude = recentMeetsThreshold ? recentPercentChange : 0;

	// Weighted score: recent direction weighted 70%, hour direction weighted 30%
	const weightedScore = (recentDirection * 0.7 * recentMagnitude) + 
	                      (hourDirection * 0.3 * hourMagnitude);

	// Determine final direction based on weighted score (increased threshold to reduce false positives)
	if (weightedScore > 0.005) {
		return 1; // Price is increasing
	} else if (weightedScore < -0.005) {
		return -1; // Price is decreasing
	}

	return 0; // No movement / stable trend
}

export function getComparator(order, orderBy) {
	return order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

export const convertDecimalToAmerican = (decimalOdds) => {
	if (decimalOdds >= 2) {
		return (decimalOdds - 1) * 100;
	} else if (decimalOdds) {
		return -100 / (decimalOdds - 1);
	} else {return null}
};

export const convertImpliedToAmericanOdds = (probability) => {
	try {
		if (probability <= 0 || probability >= 1) {
			throw new Error('Probability must be between 0 and 1 (exclusive)');
		}

		const percentage = probability * 100;
		const decimalOdds = 100 / percentage;

		let americanOdds;
		if (probability < 0.5) {
			// Underdog: positive odds
			americanOdds = Math.round((decimalOdds - 1) * 100);
			return `+${americanOdds}`;
		} else {
			// Favorite: negative odds
			americanOdds = Math.round(-100 / (decimalOdds - 1));
			return `${americanOdds}`;
		}
	} catch (e) {
		console.error('e: ', e);
		return '';
	}
};

export const convertImpliedToAmericanOddsAbsoluteValue = (probability) => {
	try {
		if (probability <= 0 || probability >= 1) {
			throw new Error('Probability must be between 0 and 1 (exclusive)');
		}

		const percentage = probability * 100;
		const decimalOdds = 100 / percentage;

		let americanOdds;
		if (probability < 0.5) {
			// Underdog: positive odds
			americanOdds = Math.round((decimalOdds - 1) * 100);
			return Math.abs(americanOdds);
		} else {
			// Favorite: negative odds
			americanOdds = Math.round(-100 / (decimalOdds - 1));
			return Math.abs(americanOdds);
		}
	} catch (e) {
		// console.error('e: ', e);
		return '';
	}
};

export const minutesSince = (date) => {
	//@ts-ignore
	const seconds = Math.floor((new Date() - date) / 1000);
	// const interval = ((seconds / 60) / 60).toFixed(1);
	const interval = seconds / 60;
	// return Math.floor(interval) + " minutes";
	return Math.floor(interval);
	// return Math.floor(interval) - 300;
};

export function getRandomColor() {
	var letters = '0123456789ABCDEF';
	var color = '#';
	for (var i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	return color;
}

// Export confidence calculator functions
export { 
	calculateOverallConfidence, 
	calculateConfidenceWithBreakdown,
	calculateSharpBookCoverage,
	getConfidenceLevel 
};
